/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: 安全场景选择Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-12 11:43:20
 */
import { useCallback, useState, useRef, useEffect } from 'react'
import { App } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { fetchAIRecognitionObjs } from '../../../actions/aiRecognition'
import {
  XCSafetyScenario,
  XCSelectionInfo,
  XC_RECOGNITION_STATUS
} from '../types'
import { useAIRecognition } from '../context/AIRecognitionContext'
import { setLoading } from '../../../actions/main'
import { useSelectedImage } from './useSelectedImage'
import useSensorTracking from './useSensorTracking'

/**
 * 安全场景选择Hook的返回类型
 */
export interface XCUseSafetyScenarioSelectionReturn {
  /** 安全场景数据 */
  safetyScenarios: XCSafetyScenario[]
  /** 安全场景选择Modal显示状态 */
  showSafetyScenarioModal: boolean
  /** 显示安全场景选择Modal */
  showSafetyScenarioSelection: (
    selectionInfo: XCSelectionInfo[]
  ) => Promise<void>
  /** 处理安全场景确认 */
  handleSafetyScenarioConfirm: (
    selectedItems: XCSafetyScenario[],
    rememberChoice: boolean,
    dataSource: XCSafetyScenario[]
  ) => void
  /** 处理安全场景取消 */
  handleSafetyScenarioCancel: () => void
}

/**
 * 安全场景选择Hook
 * 封装安全场景识别、选择和确认的逻辑
 */
export function useSafetyScenarioSelection(): XCUseSafetyScenarioSelectionReturn {
  const { message } = App.useApp()
  const dispatch = useAppDispatch()

  const {
    userId,
    selectedImages,
    setSelectedSafetyScenarios,
    setRecognitionStatus,
    recognitionStatus,
    selectionInfo,
    showSafetyScenarioModal,
    setShowSafetyScenarioModal,
    setSafetyScenarioList,
    setRememberChoice
  } = useAIRecognition()

  const { handleRecognition } = useSelectedImage()
  const { reportAISafetyHazardsClick } = useSensorTracking()

  // 使用 ref 避免闭包陷阱
  const selectedImagesRef = useRef<string[]>(selectedImages)
  const recognitionStatusRef = useRef(recognitionStatus)
  const selectionInfoRef = useRef(selectionInfo)

  // 同步更新 recognitionStatusRef
  useEffect(() => {
    recognitionStatusRef.current = recognitionStatus
  }, [recognitionStatus])

  useEffect(() => {
    selectedImagesRef.current = selectedImages
  }, [selectedImages])

  useEffect(() => {
    selectionInfoRef.current = selectionInfo
  }, [selectionInfo])

  // 安全场景数据状态
  const [safetyScenarios, setSafetyScenarios] = useState<XCSafetyScenario[]>([])

  // 从Redux store获取modelName
  const buildingSafetyAIModelName = useAppSelector(
    (state) => state.main.abTestConfig?.buildingSafetyAIModelName
  )

  /**
   * 显示安全场景选择Modal
   */
  const showSafetyScenarioSelection = useCallback(
    async (selectionInfo: XCSelectionInfo[]) => {
      if (selectionInfo.length === 0) {
        message.error('请先拖动选择关注的图片区域')
        return
      }
      dispatch(setLoading(true))

      console.log('selectionInfo------', selectionInfo)

      try {
        // 调用安全场景识别接口获取隐患场景数据
        const requestData = {
          modelName: buildingSafetyAIModelName ?? '3B+10B_v0.3',
          bbox:
            selectionInfo?.map((info) => [
              info.x,
              info.y,
              info.width,
              info.height
            ]) ?? [], // 将选区信息转换为 Array<Array<number>> 格式
          url: selectedImagesRef.current[0] || '' // 图片地址，从选中的图片中获取
        }

        const result = await dispatch(fetchAIRecognitionObjs(requestData))
        console.log('安全场景识别结果:', result)

        // 处理识别结果，将接口数据转换为GenericSelectorModal需要的格式
        if (result) {
          // 根据接口返回的数据更新安全场景
          const scenarios = result.objs.map((item: any, index: number) => ({
            id: String(index + 1), // 使用索引作为id，转换为字符串
            name: item.scene.key,
            description: item.scene.value.join('、'), // 将value数组转换为描述文本
            selected: Boolean(item.selected)
          }))
          setSafetyScenarios(scenarios)
          setSafetyScenarioList(scenarios)
          dispatch(setLoading(false))
        }
      } catch (error) {
        dispatch(setLoading(false))
        console.error('获取安全场景数据失败:', error)
        message.error('获取安全场景数据失败，请重试')
      }

      // 设置Modal可见
      setShowSafetyScenarioModal(true)
    },
    [dispatch, buildingSafetyAIModelName, setShowSafetyScenarioModal]
  )

  /**
   * 处理安全场景确认
   */
  const handleSafetyScenarioConfirm = useCallback(
    (
      selectedScenarios: XCSafetyScenario[],
      rememberChoice: boolean,
      dataSource: XCSafetyScenario[]
    ) => {
      console.log('Selected safety scenarios:', selectedScenarios)

      reportAISafetyHazardsClick({
        clickItem: 'SafetyScenarioConfirm',
        infoJson2: JSON.stringify(selectedScenarios),
        infoJson: JSON.stringify(
          selectionInfo?.map((info) => [
            info.x,
            info.y,
            info.width,
            info.height
          ]) ?? []
        )
      })

      if (selectedScenarios.length > 0) {
        // 处理自定义内容，将其转换为标准的XCSafetyScenario格式
        const processedScenarios = selectedScenarios.map((scenario) => {
          // 检查是否是自定义选项
          if (scenario.id === '__custom__') {
            const customContent = (scenario as any).customContent || ''
            console.log(
              'Processing custom scenario:',
              scenario,
              'customContent:',
              customContent
            )
            return {
              id: '__custom__',
              name: '自定义',
              description: customContent,
              selected: true
            } as XCSafetyScenario
          }
          return scenario
        })

        // 将处理后的安全隐患场景保存到context中
        setSelectedSafetyScenarios(processedScenarios)
        setRememberChoice(rememberChoice)
        setShowSafetyScenarioModal(false)

        // 设置识别状态为完成
        setRecognitionStatus(XC_RECOGNITION_STATUS.RECOGNIZING)
        // 直接传递处理后的场景数据，避免异步状态更新问题
        handleRecognition(
          selectedImagesRef.current,
          userId ?? '',
          selectionInfoRef.current ?? [],
          processedScenarios
        )
      } else {
        message.error('请选择安全场景')
      }
    },
    [
      setSelectedSafetyScenarios,
      setRecognitionStatus,
      handleRecognition,
      setShowSafetyScenarioModal
    ]
  )

  /**
   * 处理安全场景取消
   */
  const handleSafetyScenarioCancel = useCallback(() => {
    setShowSafetyScenarioModal(false)
  }, [setShowSafetyScenarioModal])

  return {
    safetyScenarios,
    showSafetyScenarioModal,
    showSafetyScenarioSelection,
    handleSafetyScenarioConfirm,
    handleSafetyScenarioCancel
  }
}
